/**
 * Slash commands system for the TipTap editor.
 *
 * Self-contained module: command definitions, filtering, React menu,
 * suggestion renderer, and the TipTap Extension.
 *
 * Module-level state (_uploadHandler, _features) is injected from outside
 * via setSlashContext() — called by RichTextField (uploadHandler) and
 * EditorMenus (features). This avoids circular deps with the main component.
 */

import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Extension } from '@tiptap/core';
import Suggestion from '@tiptap/suggestion';
import { isEditorSafeUrl } from './rich-text-constants';
import { largeurUtile } from '../../shell/cornerDock';
import {
    AlertOctagon,
    AlertTriangle,
    Calendar,
    CheckSquare,
    ChevronRight,
    CircleCheck,
    Columns2,
    Columns3,
    Heading1,
    Heading2,
    Heading3,
    Heading4,
    Heading5,
    Heading6,
    ImageIcon,
    Info,
    Lightbulb,
    List,
    ListChecks,
    ListOrdered,
    Minus,
    PanelLeft,
    PanelRight,
    Paperclip,
    Pilcrow,
    ShieldAlert,
    Smile,
    StickyNote,
    Table as TableIcon,
    Tag,
    Terminal,
    TextQuote,
} from 'lucide-react';

/* ================================================================
   Module-level state — set externally via setSlashContext()
   ================================================================ */

let _uploadHandler = null;
let _features = null;

/**
 * Injects runtime context into the slash commands module.
 * Called from RichTextField (uploadHandler) and EditorMenus (features).
 * @param {{ uploadHandler?: Function|null, features?: Set<string>|null }} ctx
 */
export function setSlashContext({ uploadHandler, features } = {}) {
    if (uploadHandler !== undefined) _uploadHandler = uploadHandler;
    if (features !== undefined) _features = features;
}

/** Resets module-level state. Call on component unmount. */
export function clearSlashContext() {
    _uploadHandler = null;
    _features = null;
}

function openFilePicker(editor, accept) {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept || '';
    input.multiple = true;
    input.onchange = () => {
        const files = Array.from(input.files || []);
        for (const file of files) {
            _uploadHandler?.(editor, file);
        }
    };
    input.click();
}

/* ================================================================
   Slash commands — items definition
   ================================================================ */

const SLASH_COMMAND_ITEMS = [
    {
        group: 'FORMAT',
        items: [
            {
                title: 'Titre 1',
                description: 'Grand titre de section',
                searchTerms: ['h1', 'heading', 'titre'],
                icon: Heading1,
                command: ({ editor, range }) => {
                    editor.chain().focus().deleteRange(range).setNode('heading', { level: 1 }).run();
                },
            },
            {
                title: 'Titre 2',
                description: 'Moyen titre de section',
                searchTerms: ['h2', 'heading', 'sous-titre'],
                icon: Heading2,
                command: ({ editor, range }) => {
                    editor.chain().focus().deleteRange(range).setNode('heading', { level: 2 }).run();
                },
            },
            {
                title: 'Titre 3',
                description: 'Petit titre de section',
                searchTerms: ['h3', 'heading'],
                icon: Heading3,
                command: ({ editor, range }) => {
                    editor.chain().focus().deleteRange(range).setNode('heading', { level: 3 }).run();
                },
            },
            {
                title: 'Titre 4',
                description: 'Sous-titre niveau 4',
                searchTerms: ['h4', 'heading'],
                icon: Heading4,
                command: ({ editor, range }) => {
                    editor.chain().focus().deleteRange(range).setNode('heading', { level: 4 }).run();
                },
            },
            {
                title: 'Titre 5',
                description: 'Sous-titre niveau 5',
                searchTerms: ['h5', 'heading'],
                icon: Heading5,
                command: ({ editor, range }) => {
                    editor.chain().focus().deleteRange(range).setNode('heading', { level: 5 }).run();
                },
            },
            {
                title: 'Titre 6',
                description: 'Sous-titre niveau 6',
                searchTerms: ['h6', 'heading'],
                icon: Heading6,
                command: ({ editor, range }) => {
                    editor.chain().focus().deleteRange(range).setNode('heading', { level: 6 }).run();
                },
            },
            {
                title: 'Texte',
                description: 'Bloc de paragraphe',
                searchTerms: ['paragraph', 'texte', 'normal'],
                icon: Pilcrow,
                command: ({ editor, range }) => {
                    editor.chain().focus().deleteRange(range).setNode('paragraph').run();
                },
            },
            {
                title: 'Citation',
                description: 'Ajouter une section de citation',
                searchTerms: ['blockquote', 'quote', 'citation'],
                icon: TextQuote,
                command: ({ editor, range }) => {
                    editor.chain().focus().deleteRange(range).toggleBlockquote().run();
                },
            },
            {
                title: 'Code',
                description: 'Ajouter une section de code',
                searchTerms: ['code', 'codeblock', 'pre'],
                icon: Terminal,
                command: ({ editor, range }) => {
                    editor.chain().focus().deleteRange(range).toggleCodeBlock().run();
                },
            },
        ],
    },
    {
        group: 'LISTES',
        items: [
            {
                title: 'Liste à puces',
                description: 'Créer une liste non ordonnée',
                searchTerms: ['bullet', 'list', 'puces', 'ul'],
                icon: List,
                command: ({ editor, range }) => {
                    editor.chain().focus().deleteRange(range).toggleBulletList().run();
                },
            },
            {
                title: 'Liste numérotée',
                description: 'Créer une liste ordonnée',
                searchTerms: ['ordered', 'numbered', 'ol', 'numerotee'],
                icon: ListOrdered,
                command: ({ editor, range }) => {
                    editor.chain().focus().deleteRange(range).toggleOrderedList().run();
                },
            },
            {
                title: 'Liste de tâches',
                description: 'Créer une checklist avec cases à cocher',
                searchTerms: ['task', 'todo', 'checklist', 'tache'],
                icon: CheckSquare,
                command: ({ editor, range }) => {
                    editor.chain().focus().deleteRange(range).toggleTaskList().run();
                },
            },
        ],
    },
    {
        group: 'BLOCS',
        items: [
            {
                title: 'Info',
                description: 'Bloc informatif bleu',
                searchTerms: ['callout', 'info', 'note', 'bloc'],
                icon: Info,
                command: ({ editor, range }) => {
                    editor.chain().focus().deleteRange(range).insertCallout({ type: 'info' }).run();
                },
            },
            {
                title: 'Attention',
                description: 'Bloc d\'avertissement',
                searchTerms: ['callout', 'warning', 'attention', 'avertissement'],
                icon: AlertTriangle,
                command: ({ editor, range }) => {
                    editor.chain().focus().deleteRange(range).insertCallout({ type: 'warning' }).run();
                },
            },
            {
                title: 'Danger',
                description: 'Bloc d\'alerte critique',
                searchTerms: ['callout', 'danger', 'error', 'alerte'],
                icon: AlertOctagon,
                command: ({ editor, range }) => {
                    editor.chain().focus().deleteRange(range).insertCallout({ type: 'danger' }).run();
                },
            },
            {
                title: 'Succès',
                description: 'Bloc de confirmation',
                searchTerms: ['callout', 'success', 'succes', 'ok'],
                icon: CircleCheck,
                command: ({ editor, range }) => {
                    editor.chain().focus().deleteRange(range).insertCallout({ type: 'success' }).run();
                },
            },
            {
                title: 'Note',
                description: 'Bloc de note discrète',
                searchTerms: ['callout', 'note', 'memo'],
                icon: StickyNote,
                command: ({ editor, range }) => {
                    editor.chain().focus().deleteRange(range).insertCallout({ type: 'note' }).run();
                },
            },
            {
                title: 'Astuce',
                description: 'Conseil ou bonne pratique',
                searchTerms: ['callout', 'tip', 'astuce', 'conseil'],
                icon: Lightbulb,
                command: ({ editor, range }) => {
                    editor.chain().focus().deleteRange(range).insertCallout({ type: 'tip' }).run();
                },
            },
            {
                title: 'Précaution',
                description: 'Point de vigilance',
                searchTerms: ['callout', 'caution', 'precaution', 'vigilance'],
                icon: ShieldAlert,
                command: ({ editor, range }) => {
                    editor.chain().focus().deleteRange(range).insertCallout({ type: 'caution' }).run();
                },
            },
            {
                title: 'Prérequis',
                description: 'Conditions préalables',
                searchTerms: ['callout', 'prerequisite', 'prerequis', 'requis'],
                icon: ListChecks,
                command: ({ editor, range }) => {
                    editor.chain().focus().deleteRange(range).insertCallout({ type: 'prerequisite' }).run();
                },
            },
            {
                title: 'Bloc repliable',
                description: 'Contenu pliable / dépliant',
                searchTerms: ['details', 'toggle', 'accordion', 'repliable', 'collapse'],
                icon: ChevronRight,
                command: ({ editor, range }) => {
                    editor.chain().focus().deleteRange(range).insertDetails({ summary: 'Cliquer pour développer' }).run();
                },
            },
            {
                title: 'Séparateur',
                description: 'Ligne horizontale de séparation',
                searchTerms: ['hr', 'separator', 'divider', 'separateur', 'ligne'],
                icon: Minus,
                command: ({ editor, range }) => {
                    editor.chain().focus().deleteRange(range).setHorizontalRule().run();
                },
            },
        ],
    },
    {
        group: 'MISE EN PAGE',
        items: [
            {
                title: '2 colonnes',
                description: 'Diviser en deux colonnes égales',
                searchTerms: ['colonnes', 'columns', 'layout', '2col', 'cote'],
                icon: Columns2,
                command: ({ editor, range }) => {
                    editor.chain().focus().deleteRange(range).insertColumns({ columns: 2, layout: 'equal-2' }).run();
                },
            },
            {
                title: '3 colonnes',
                description: 'Diviser en trois colonnes égales',
                searchTerms: ['colonnes', 'columns', 'layout', '3col'],
                icon: Columns3,
                command: ({ editor, range }) => {
                    editor.chain().focus().deleteRange(range).insertColumns({ columns: 3, layout: 'equal-3' }).run();
                },
            },
            {
                title: 'Colonnes gauche large',
                description: 'Grande colonne à gauche, petite à droite',
                searchTerms: ['colonnes', 'sidebar', 'gauche', 'wide', 'large'],
                icon: PanelLeft,
                command: ({ editor, range }) => {
                    editor.chain().focus().deleteRange(range).insertColumns({ columns: 2, layout: 'left-wide' }).run();
                },
            },
            {
                title: 'Colonnes droite large',
                description: 'Petite colonne à gauche, grande à droite',
                searchTerms: ['colonnes', 'sidebar', 'droite', 'wide', 'large'],
                icon: PanelRight,
                command: ({ editor, range }) => {
                    editor.chain().focus().deleteRange(range).insertColumns({ columns: 2, layout: 'right-wide' }).run();
                },
            },
        ],
    },
    {
        group: 'INSERTIONS',
        items: [
            {
                title: 'Image',
                description: 'Insérer une image',
                searchTerms: ['image', 'photo', 'picture', 'img', 'upload'],
                icon: ImageIcon,
                command: ({ editor, range }) => {
                    editor.chain().focus().deleteRange(range).run();
                    if (_uploadHandler) {
                        openFilePicker(editor, 'image/*');
                    } else {
                        const url = window.prompt('URL de l\'image :');
                        if (!url || !isEditorSafeUrl(url)) return;
                        const alt = window.prompt('Texte alternatif (optionnel) :') || '';
                        editor.chain().focus().setImage({ src: url, alt: alt.trim() || undefined }).run();
                    }
                },
            },
            {
                title: 'Fichier',
                description: 'Joindre un fichier téléchargeable',
                searchTerms: ['fichier', 'file', 'attachment', 'upload', 'pdf', 'document', 'pièce jointe'],
                icon: Paperclip,
                uploadOnly: true,
                command: ({ editor, range }) => {
                    editor.chain().focus().deleteRange(range).run();
                    if (_uploadHandler) {
                        openFilePicker(editor, '');
                    }
                },
            },
            {
                title: 'Tableau',
                description: 'Insérer un tableau 3×3',
                searchTerms: ['table', 'tableau', 'grid'],
                icon: TableIcon,
                command: ({ editor, range }) => {
                    editor.chain().focus().deleteRange(range)
                        .insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
                },
            },
            {
                title: 'Emoji',
                description: 'Insérer un emoji (tapez : pour chercher)',
                searchTerms: ['emoji', 'smiley', 'emoticone', 'emoticon'],
                icon: Smile,
                featureFlag: 'emoji',
                command: ({ editor, range }) => {
                    editor.chain().focus().deleteRange(range).insertContent(':').run();
                },
            },
            {
                title: 'Date',
                description: 'Insérer une date inline',
                searchTerms: ['date', 'calendrier', 'calendar', 'jour'],
                icon: Calendar,
                featureFlag: 'inlineDate',
                command: ({ editor, range }) => {
                    editor.chain().focus().deleteRange(range).insertContent({
                        type: 'inlineDate',
                        attrs: { date: new Date().toISOString().slice(0, 10) },
                    }).run();
                },
            },
            {
                title: 'Statut',
                description: 'Insérer un badge de statut',
                searchTerms: ['statut', 'status', 'badge', 'label', 'tag'],
                icon: Tag,
                featureFlag: 'statusBadge',
                command: ({ editor, range }) => {
                    editor.chain().focus().deleteRange(range).insertContent({
                        type: 'statusBadge',
                        attrs: { label: 'Statut', color: 'default' },
                    }).run();
                },
            },
        ],
    },
];

function getFilteredSlashItems(query) {
    const q = query.toLowerCase();
    const hasUpload = !!_uploadHandler;
    const feats = _features;
    const results = [];
    for (const group of SLASH_COMMAND_ITEMS) {
        const filtered = group.items.filter((item) => {
            if (item.uploadOnly && !hasUpload) return false;
            if (item.featureFlag && feats && !feats.has(item.featureFlag)) return false;
            if (item.title.toLowerCase().includes(q)) return true;
            if (item.description.toLowerCase().includes(q)) return true;
            return item.searchTerms.some((t) => t.includes(q));
        });
        if (filtered.length) results.push({ group: group.group, items: filtered });
    }
    return results;
}

/* ================================================================
   Slash commands — React menu component
   ================================================================ */

const SlashCommandsMenu = forwardRef(function SlashCommandsMenu({ items, command }, ref) {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const menuRef = useRef(null);
    const selectedRef = useRef(0);

    const flatItems = items.flatMap((g) => g.items);
    const flatItemsRef = useRef(flatItems);
    flatItemsRef.current = flatItems;

    useEffect(() => {
        setSelectedIndex(0);
        selectedRef.current = 0;
    }, [items]);

    useLayoutEffect(() => {
        selectedRef.current = selectedIndex;
        const active = menuRef.current?.querySelector('[data-active="true"]');
        if (active) active.scrollIntoView({ block: 'nearest' });
    }, [selectedIndex]);

    useImperativeHandle(ref, () => ({
        onKeyDown({ event }) {
            const list = flatItemsRef.current;
            if (event.key === 'ArrowUp') {
                setSelectedIndex((i) => {
                    const next = i <= 0 ? list.length - 1 : i - 1;
                    selectedRef.current = next;
                    return next;
                });
                return true;
            }
            if (event.key === 'ArrowDown') {
                setSelectedIndex((i) => {
                    const next = i >= list.length - 1 ? 0 : i + 1;
                    selectedRef.current = next;
                    return next;
                });
                return true;
            }
            if (event.key === 'Enter') {
                const item = list[selectedRef.current];
                if (item) command(item);
                return true;
            }
            return false;
        },
    }));

    if (!flatItems.length) {
        return (
            <div className="bg-white text-stone-500 rounded-xl shadow-xl border border-stone-200 p-4 text-sm">
                Aucun résultat
            </div>
        );
    }

    let globalIdx = 0;

    return (
        <div
            ref={menuRef}
            className="bg-white rounded-xl shadow-xl border border-stone-200 py-2 min-w-[260px] max-h-[380px] overflow-y-auto"
        >
            {items.map((group) => (
                <div key={group.group}>
                    <div className="px-3 pt-2 pb-1 text-3xs font-bold tracking-wider text-stone-400 uppercase select-none">
                        {group.group}
                    </div>
                    {group.items.map((item) => {
                        const idx = globalIdx++;
                        const isActive = idx === selectedIndex;
                        const Icon = item.icon;
                        return (
                            <button
                                key={item.title}
                                type="button"
                                data-active={isActive}
                                className={`flex items-center gap-3 w-full text-left px-3 py-2 text-sm cursor-pointer transition-colors ${
                                    isActive
                                        ? 'bg-stone-100 text-stone-900'
                                        : 'text-stone-700 hover:bg-stone-50 hover:text-stone-900'
                                }`}
                                onClick={() => command(item)}
                            >
                                <span className={`flex items-center justify-center w-8 h-8 rounded-lg shrink-0 ${
                                    isActive ? 'bg-stone-200' : 'bg-stone-100'
                                }`}>
                                    <Icon size={16} />
                                </span>
                                <span className="flex flex-col min-w-0">
                                    <span className="font-medium leading-tight">{item.title}</span>
                                    <span className="text-xs text-stone-400 leading-tight truncate">{item.description}</span>
                                </span>
                            </button>
                        );
                    })}
                </div>
            ))}
        </div>
    );
});

/* ================================================================
   Slash commands — suggestion render adapter (React <-> TipTap)
   ================================================================ */

/**
 * Creates the TipTap suggestion renderer for slash commands.
 * Returns the { onStart, onUpdate, onKeyDown, onExit } adapter
 * that bridges the TipTap suggestion plugin with the React SlashCommandsMenu.
 */
export function createSlashSuggestionRenderer() {
    let root = null;
    let popup = null;
    let componentRef = { current: null };

    return {
        onStart(props) {
            popup = document.createElement('div');
            popup.style.position = 'fixed';
            popup.style.zIndex = '9999';
            popup.style.visibility = 'hidden';
            document.body.appendChild(popup);
            updatePopup(props);
        },

        onUpdate(props) {
            updatePopup(props);
        },

        onKeyDown(props) {
            if (props.event.key === 'Escape') {
                destroyPopup();
                return true;
            }
            return componentRef.current?.onKeyDown(props) ?? false;
        },

        onExit() {
            destroyPopup();
        },
    };

    function updatePopup(props) {
        if (!popup) return;

        const rect = props.clientRect?.();
        if (rect) {
            const menuHeight = 400;
            const menuWidth = 280;
            const spaceBelow = window.innerHeight - rect.bottom;
            const top = spaceBelow < menuHeight && rect.top > menuHeight
                ? rect.top - menuHeight - 4
                : rect.bottom + 4;
            const left = Math.min(rect.left, largeurUtile() - menuWidth - 8);
            popup.style.left = `${Math.max(8, left)}px`;
            popup.style.top = `${top}px`;
            popup.style.visibility = 'visible';
        }

        const groupedItems = getFilteredSlashItems(props.query);

        if (!root) {
            root = createRoot(popup);
        }

        root.render(
            <SlashCommandsMenu
                ref={componentRef}
                items={groupedItems}
                command={(item) => {
                    props.command(item);
                }}
            />
        );
    }

    function destroyPopup() {
        if (root) {
            root.unmount();
            root = null;
        }
        if (popup) {
            popup.remove();
            popup = null;
        }
    }
}

/* ================================================================
   Slash commands — TipTap extension
   ================================================================ */

/** TipTap extension that wires the `/` trigger to the suggestion plugin. */
export const SlashCommands = Extension.create({
    name: 'slashCommands',

    addOptions() {
        return { suggestion: {} };
    },

    addProseMirrorPlugins() {
        return [
            Suggestion({
                editor: this.editor,
                char: '/',
                startOfLine: false,
                allowSpaces: false,
                allow({ state, range }) {
                    const $from = state.doc.resolve(range.from);
                    const parentType = $from.parent.type.name;
                    const blockedTypes = ['codeBlock', 'heading'];
                    return !blockedTypes.includes(parentType);
                },
                command({ editor, range, props: item }) {
                    item.command({ editor, range });
                },
                items({ query }) {
                    return getFilteredSlashItems(query).flatMap((g) => g.items);
                },
                ...this.options.suggestion,
            }),
        ];
    },
});
